// @ts-strict-ignore
import React, { useContext, useMemo } from 'react';
import AggregationSelect from './AggregationSelect';
import {
  StyledAggregationSection,
  StyledAggregationCriteriaRow,
  StyledAggregationClause,
  StyledTimeframeNumberInput,
} from './styles';
import { AggregationEditorContext } from './context';
import { getIsPreview } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/helpers';
import { ThresholdContext } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/Threshold/context';
import { getMultiSelectProps } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/Threshold/helpers';
import { RuleBuilderContext } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/context';
import { useAppSelector } from '~/store/reduxHooks';
import { selectRuleBuilderActiveSubEditor } from 'flow/store/redux/selectors/ui';

import { SCOPE_OPTIONS, TIMEFRAME_UNIT_OPTIONS } from './constants';
import { getAggregationTypeOptions, reconcileAggregationType } from './helpers';
import type { AggregationSelectFieldKey } from './types';
import type { AggregationOption } from './AggregationSelect';
import type { TimeframeUnit } from 'flow/store/engine/constants';
import { AggregationEditorOrigin } from '../NodeEditorDrawer/types';

const BaseAggregationsSection = () => {
  const { sourceData, setSourceData, origin } = useContext(AggregationEditorContext);
  const { editMode, containerType } = useContext(RuleBuilderContext);
  const { populatedThreshold } = useContext(ThresholdContext);
  const activeSubEditor = useAppSelector(selectRuleBuilderActiveSubEditor);

  const thresholdSide = origin === AggregationEditorOrigin.THRESHOLD_LEFT ? 'left' : 'right';
  const attribute = populatedThreshold?.[thresholdSide]?.attribute;

  const columnSlugOptions = useMemo(
    () => attribute?.aggregationColumnOptions ?? [],
    [attribute?.aggregationColumnOptions],
  );
  const aggregationTypeOptions = useMemo(
    () => getAggregationTypeOptions(columnSlugOptions, sourceData.columnSlug),
    [columnSlugOptions, sourceData.columnSlug],
  );

  const multiSelectProps = getMultiSelectProps({
    action: populatedThreshold?.[thresholdSide]?.action,
    options: {
      isPreview: populatedThreshold
        ? getIsPreview(editMode, populatedThreshold, activeSubEditor.thresholdId)
        : false,
    },
  });

  const handleSelectChange = (field: AggregationSelectFieldKey) => (option: AggregationOption) => {
    const { value } = option;
    switch (field) {
      case 'timeframeUnit':
        setSourceData(prev => ({ ...prev, [field]: value as TimeframeUnit }));
        break;
      case 'columnSlug':
        setSourceData(prev => ({
          ...prev,
          columnSlug: value,
          aggregationType: reconcileAggregationType(
            columnSlugOptions, value, prev.aggregationType,
          ),
        }));
        break;
      case 'aggregationType':
      case 'scope':
        setSourceData(prev => ({ ...prev, [field]: value }));
        break;
    }
  };

  const handleTimeframeNumberChange = (field: 'timeframeIntervalStart' | 'timeframeIntervalEnd') => (newValue: number) => {
    setSourceData(prev => ({ ...prev, [field]: newValue }));
  };

  return (
    <StyledAggregationSection>
      <StyledAggregationCriteriaRow>
        <AggregationSelect
          multiSelectProps={multiSelectProps}
          options={aggregationTypeOptions}
          value={sourceData.aggregationType}
          onChange={handleSelectChange('aggregationType')}
        />
        <StyledAggregationClause containerType={containerType}>
          of
        </StyledAggregationClause>
        <AggregationSelect
          multiSelectProps={multiSelectProps}
          options={columnSlugOptions}
          value={sourceData.columnSlug}
          onChange={handleSelectChange('columnSlug')}
        />
        <StyledAggregationClause containerType={containerType}>
          with the following criteria:
        </StyledAggregationClause>
      </StyledAggregationCriteriaRow>

      <StyledAggregationCriteriaRow>
        <StyledAggregationClause containerType={containerType}>
          Scope:
        </StyledAggregationClause>
        <AggregationSelect
          multiSelectProps={multiSelectProps}
          options={SCOPE_OPTIONS}
          value={sourceData.scope}
          onChange={handleSelectChange('scope')}
        />
      </StyledAggregationCriteriaRow>

      <StyledAggregationCriteriaRow>
        <StyledAggregationClause containerType={containerType}>
          Timeframe:
        </StyledAggregationClause>
        <StyledTimeframeNumberInput
          minValue={0}
          maxValue={999}
          value={sourceData.timeframeIntervalStart}
          onHandleValueChange={handleTimeframeNumberChange('timeframeIntervalStart')}
        />
        <StyledAggregationClause containerType={containerType}>
          to
        </StyledAggregationClause>
        <StyledTimeframeNumberInput
          minValue={sourceData.timeframeIntervalStart}
          // TODO: This should be dynamic based on the attribute/units (e.g. days, weeks, months, years)
          maxValue={999}
          value={sourceData.timeframeIntervalEnd}
          onHandleValueChange={handleTimeframeNumberChange('timeframeIntervalEnd')}
        />
        <AggregationSelect
          multiSelectProps={multiSelectProps}
          options={TIMEFRAME_UNIT_OPTIONS}
          value={sourceData.timeframeUnit}
          onChange={handleSelectChange('timeframeUnit')}
        />
        <StyledAggregationClause containerType={containerType}>
          ago
        </StyledAggregationClause>
      </StyledAggregationCriteriaRow>
    </StyledAggregationSection>
  );
};

export default BaseAggregationsSection;
